import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAuth } from '../hooks/useAuth';
import { getUsers, setup } from '../services/auth';

export default function Login() {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }
    loadUsers();
  }, [isAuthenticated, navigate]);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      if (data.users && data.users.length > 0) {
        setUsers(data.users);
        setNeedsSetup(false);
      } else {
        setNeedsSetup(true);
      }
    } catch (err) {
      // An API/network error is NOT the same as "needs setup".
      // Show a recoverable error so the user knows to retry.
      const status = err.response?.status;
      if (status === 429) {
        setError('Too many requests. Please wait a moment and refresh the page.');
      } else {
        setError('Could not reach the server. Please refresh the page.');
      }
      setNeedsSetup(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(password, selectedUser);
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed';
      if (err.response?.status === 429) {
        setError('Too many login attempts. Please wait 15 minutes.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await setup(password, [
        { username: 'Nathan', email: 'nathan@wadedahl.com' },
        { username: 'Jasara', email: 'jasara@wadedahl.com' },
      ]);
      await loadUsers();
      setPassword('');
      setNeedsSetup(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo / Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <AccountBalanceWalletIcon
              sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
            />
            <Typography variant="h5" fontWeight={700}>
              Family Budget
            </Typography>
            <Typography variant="body2" color="text.secondary">
              WadeDahl Family
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {needsSetup ? (
            /* First-time setup form */
            <form onSubmit={handleSetup}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                First-Time Setup
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create a family password to secure your budget app.
              </Typography>

              <TextField
                fullWidth
                label="Family Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Min 12 characters, with uppercase, lowercase, number, and special character.
              </Typography>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Set Up'}
              </Button>
            </form>
          ) : (
            /* Normal login form */
            <form onSubmit={handleLogin}>
              {/* User selector */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Who's logging in?
              </Typography>
              <ToggleButtonGroup
                value={selectedUser}
                exclusive
                onChange={(_, val) => { if (val !== null) setSelectedUser(val); }}
                fullWidth
                sx={{ mb: 3 }}
              >
                {users.map((u) => (
                  <ToggleButton
                    key={u.id}
                    value={u.id}
                    sx={{
                      py: 1.5,
                      fontWeight: selectedUser === u.id ? 700 : 400,
                    }}
                  >
                    {u.username}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Divider sx={{ mb: 2 }} />

              <TextField
                fullWidth
                label="Family Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !selectedUser}
              >
                {loading ? <CircularProgress size={24} /> : 'Log In'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
